import React, { useState, useRef } from 'react';
import { Plus, Camera, Upload, Sparkles } from 'lucide-react';

interface CreateObjectButtonProps {
  onCreateFromPhoto: (photo: string) => void;
  onCreateManual: () => void;
  className?: string;
}

const CreateObjectButton: React.FC<CreateObjectButtonProps> = ({
  onCreateFromPhoto,
  onCreateManual,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          // Simuler la reconnaissance d'image
          const suggestions = simulateImageRecognition(result);
          onCreateFromPhoto(result);
        }
      };
      reader.readAsDataURL(file);
    }
    setIsOpen(false);
  };

  const simulateImageRecognition = (imageData: string) => {
    // Simulation de la reconnaissance d'image
    const suggestions = [
      { nom: 'Ballon de football', categorie: 'Jeux extérieurs', sousCategorie: 'Ballons & sports', confidence: 0.95 },
      { nom: 'Microscope', categorie: 'Matériel électronique', sousCategorie: 'Instruments', confidence: 0.87 },
      { nom: 'Livre de contes', categorie: 'Pédagogie', sousCategorie: 'Livres', confidence: 0.82 },
      { nom: 'Crayon de couleur', categorie: 'Matériel créatif & manuel', sousCategorie: 'Fournitures', confidence: 0.78 }
    ];
    
    // Retourner la suggestion avec la plus haute confiance
    return suggestions[0];
  };

  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-center p-4 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 active:bg-green-800 transition-colors touch-target"
      >
        <Plus className="h-6 w-6 mr-2" />
        <span className="text-base font-medium">Nouvel objet</span>
      </button>

      {/* Menu déroulant */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-2 space-y-2">
            {/* Option 1: Prendre une photo avec reconnaissance */}
            <button
              onClick={handleCameraCapture}
              className="w-full flex items-center p-3 text-left hover:bg-gray-50 rounded-md transition-colors"
            >
              <Camera className="h-5 w-5 mr-3 text-blue-600" />
              <div>
                <div className="font-medium text-gray-900">Prendre une photo</div>
                <div className="text-sm text-gray-500">Reconnaissance automatique</div>
              </div>
              <Sparkles className="h-4 w-4 ml-auto text-yellow-500" />
            </button>

            {/* Option 2: Importer une photo */}
            <button
              onClick={handleFileUpload}
              className="w-full flex items-center p-3 text-left hover:bg-gray-50 rounded-md transition-colors"
            >
              <Upload className="h-5 w-5 mr-3 text-green-600" />
              <div>
                <div className="font-medium text-gray-900">Importer une photo</div>
                <div className="text-sm text-gray-500">Reconnaissance automatique</div>
              </div>
              <Sparkles className="h-4 w-4 ml-auto text-yellow-500" />
            </button>

            {/* Option 3: Création manuelle */}
            <button
              onClick={() => {
                onCreateManual();
                setIsOpen(false);
              }}
              className="w-full flex items-center p-3 text-left hover:bg-gray-50 rounded-md transition-colors"
            >
              <Plus className="h-5 w-5 mr-3 text-gray-600" />
              <div>
                <div className="font-medium text-gray-900">Création manuelle</div>
                <div className="text-sm text-gray-500">Saisie complète</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Inputs cachés */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
    </div>
  );
};

export default CreateObjectButton;




